import type { Parameter, ParameterEntry } from "@/types/parameter"

/** Recursively collect all descendant parameter names from a set of child names */
export function collectDescendantNames(
  childNames: string[],
  allParams: Parameter[],
): Set<string> {
  const descendants = new Set<string>()
  const collect = (names: string[]): void => {
    for (const name of names) {
      if (descendants.has(name)) continue
      descendants.add(name)
      const p = allParams.find((param) => param.name === name)
      if (p?.childParameters?.length) {
        collect(p.childParameters)
      }
    }
  }
  collect(childNames)
  return descendants
}

/** Build parent parameters dict for cascading API call */
export function getParentParameterValues(
  parameter: Parameter,
  allParameters: Parameter[],
): Record<string, string> {
  const parentValues: Record<string, string> = {}

  for (const param of allParameters) {
    if (param.name === parameter.name) continue
    // A parameter is a parent if this parameter's name appears in its childParameters
    if (param.childParameters?.includes(parameter.name)) {
      parentValues[param.name] = getParameterStringValue(param)
    }
  }

  return parentValues
}

/** Get the string value from any parameter type for API calls */
export function getParameterStringValue(param: Parameter): string {
  switch (param.type) {
    case "Bool":
      // C# backend expects PascalCase booleans ("True"/"False")
      return param.boolValue ? "True" : "False"
    case "String":
      return param.stringValue
    case "MultiSelect":
      return param.stringValues.join(",")
    case "Int":
      return String(param.intValue)
    case "DateTime":
      return param.dateTimeValue
  }
}


/** Build parameter dict for API calls from Parameter array */
export function buildParameterDict(
  parameters: Parameter[],
): Record<string, string | string[]> {
  const dict: Record<string, string | string[]> = {}
  for (const param of parameters) {
    // Telerik expects multi-value params as JSON arrays
    if (param.type === "MultiSelect") {
      dict[param.name] = param.stringValues
    } else {
      dict[param.name] = getParameterStringValue(param)
    }
  }
  return dict
}

/** Convert Parameter array to ParameterEntry array (for serialization) */
export function parametersToEntries(
  parameters: Parameter[],
): ParameterEntry[] {
  return parameters.map((param) => ({
    parameterName: param.name,
    selectedValues:
      param.type === "MultiSelect"
        ? param.stringValues
        : [getParameterStringValue(param)],
  }))
}

/** Get report file name matching Blazor's GetReportFileName */
export function getReportFileName(
  reportName: string,
  dateSuffix?: string,
): string {
  const suffix =
    dateSuffix ||
    new Date().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).replaceAll('/', "-")
  return `${reportName}_${suffix}.pdf`
}
